import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  Layout,
  Button,
  DataTable,
  Badge,
  Text,
  Modal,
  FormLayout,
  TextField,
  Select,
  Checkbox,
  EmptyState,
  Spinner,
  Toast,
  Frame,
  Tabs,
  ProgressBar,
  Banner,
  LegacyStack as Stack,
  ButtonGroup,
  InlineStack,
  BlockStack
} from '@shopify/polaris';
import {
  PlusMinor,
  EditMinor,
  RefreshIcon
} from '@shopify/polaris-icons';
import axios from 'axios';

interface RoutingConfiguration {
  id: string;
  strategy: 'ROUND_ROBIN' | 'LEAST_BUSY' | 'SKILL_BASED' | 'PRIORITY' | 'MANUAL';
  businessHoursOnly: boolean;
  businessHours?: any;
  timezone: string;
  maxConversationsPerAgent: number;
  enableLoadBalancing: boolean;
  skillBasedRouting: boolean;
  skillMatchingMode: 'ANY' | 'ALL' | 'BEST_MATCH';
  enablePriorityRouting: boolean;
  priorityRules: any[];
  fallbackStrategy: 'ROUND_ROBIN' | 'LEAST_BUSY' | 'SKILL_BASED' | 'PRIORITY' | 'MANUAL';
  assignToAnyIfUnavailable: boolean;
  isActive: boolean;
}

interface Agent {
  id: string;
  name: string;
  email: string;
  skills: string[];
  maxConversations: number;
  isAvailable: boolean;
  isActive: boolean;
}

interface AgentWorkload {
  agentId: string;
  agentName: string;
  activeConversations: number;
  maxConversations: number;
  utilization: number;
  isAvailable: boolean;
  skills: string[];
}

interface AgentPerformance {
  agentId: string;
  agentName: string;
  totalAssigned: number;
  averageResponseTime: number;
  completedConversations: number;
  activeConversations: number;
}

interface RoutingStatistics {
  totalRouted: number;
  successfulRoutes: number;
  failedRoutes: number;
  averageRoutingTime: number;
  strategyBreakdown: Record<string, number>;
}

export default function TeamRoutingPage() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [config, setConfig] = useState<RoutingConfiguration | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [workloads, setWorkloads] = useState<AgentWorkload[]>([]);
  const [performance, setPerformance] = useState<AgentPerformance[]>([]);
  const [statistics, setStatistics] = useState<RoutingStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Configuration form state
  const [configForm, setConfigForm] = useState({
    strategy: 'ROUND_ROBIN' as any,
    businessHoursOnly: false,
    timezone: 'UTC',
    maxConversationsPerAgent: 10,
    enableLoadBalancing: true,
    skillBasedRouting: false,
    skillMatchingMode: 'ANY' as any,
    enablePriorityRouting: false,
    fallbackStrategy: 'LEAST_BUSY' as any,
    assignToAnyIfUnavailable: true,
    isActive: true
  });

  // Agent form state
  const [agentForm, setAgentForm] = useState({
    agentId: '',
    skills: '',
    maxConversations: 10,
    isAvailable: true
  });

  const storeId = localStorage.getItem('storeId') || '';
  const token = localStorage.getItem('token') || '';

  useEffect(() => {
    fetchData();
  }, [selectedTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchConfiguration(),
        fetchAgents(),
        selectedTab === 1 && fetchWorkloads(),
        selectedTab === 2 && fetchPerformance(),
        selectedTab === 2 && fetchStatistics()
      ].filter(Boolean));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      showToast('Failed to load routing data');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfiguration = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/routing/config`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-store-id': storeId
          }
        }
      );
      setConfig(response.data);
      if (response.data) {
        setConfigForm({
          strategy: response.data.strategy,
          businessHoursOnly: response.data.businessHoursOnly,
          timezone: response.data.timezone,
          maxConversationsPerAgent: response.data.maxConversationsPerAgent,
          enableLoadBalancing: response.data.enableLoadBalancing,
          skillBasedRouting: response.data.skillBasedRouting,
          skillMatchingMode: response.data.skillMatchingMode,
          enablePriorityRouting: response.data.enablePriorityRouting,
          fallbackStrategy: response.data.fallbackStrategy,
          assignToAnyIfUnavailable: response.data.assignToAnyIfUnavailable,
          isActive: response.data.isActive
        });
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch configuration:', error);
      }
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/agents`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAgents(response.data);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    }
  };

  const fetchWorkloads = async () => {
    try {
      const workloadPromises = agents.map(agent =>
        axios.get(
          `${import.meta.env.VITE_API_URL}/api/routing/agent-workload/${agent.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'x-store-id': storeId
            }
          }
        )
      );
      const responses = await Promise.all(workloadPromises);
      const workloadData = responses.map((res, idx) => ({
        ...res.data,
        agentName: agents[idx].name
      }));
      setWorkloads(workloadData);
    } catch (error) {
      console.error('Failed to fetch workloads:', error);
    }
  };

  const fetchPerformance = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/routing/agent-performance`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-store-id': storeId
          }
        }
      );
      setPerformance(response.data);
    } catch (error) {
      console.error('Failed to fetch performance:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/routing/statistics?days=7`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-store-id': storeId
          }
        }
      );
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const handleConfigSubmit = async () => {
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/routing/config`,
        configForm,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-store-id': storeId
          }
        }
      );
      showToast('Routing configuration updated successfully');
      setShowConfigModal(false);
      fetchConfiguration();
    } catch (error: any) {
      console.error('Failed to update configuration:', error);
      showToast(error.response?.data?.error || 'Failed to update configuration');
    }
  };

  const handleAgentUpdate = async () => {
    try {
      const skills = agentForm.skills.split(',').map(s => s.trim()).filter(Boolean);

      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/routing/agent/${agentForm.agentId}/skills`,
        { skills },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-store-id': storeId
          }
        }
      );

      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/routing/agent/${agentForm.agentId}/max-conversations`,
        { maxConversations: agentForm.maxConversations },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-store-id': storeId
          }
        }
      );

      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/routing/agent/${agentForm.agentId}/availability`,
        { isAvailable: agentForm.isAvailable },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-store-id': storeId
          }
        }
      );

      showToast('Agent updated successfully');
      setShowAgentModal(false);
      resetAgentForm();
      fetchAgents();
      if (selectedTab === 1) fetchWorkloads();
    } catch (error: any) {
      console.error('Failed to update agent:', error);
      showToast(error.response?.data?.error || 'Failed to update agent');
    }
  };

  const handleEditAgent = (agent: Agent) => {
    setEditingAgent(agent);
    setAgentForm({
      agentId: agent.id,
      skills: agent.skills.join(', '),
      maxConversations: agent.maxConversations,
      isAvailable: agent.isAvailable
    });
    setShowAgentModal(true);
  };

  const resetAgentForm = () => {
    setAgentForm({
      agentId: '',
      skills: '',
      maxConversations: 10,
      isAvailable: true
    });
    setEditingAgent(null);
  };

  const showToast = (message: string) => {
    setToastMessage(message);
    setToastActive(true);
  };

  const strategyOptions = [
    { label: 'Round Robin', value: 'ROUND_ROBIN' },
    { label: 'Least Busy', value: 'LEAST_BUSY' },
    { label: 'Skill Based', value: 'SKILL_BASED' },
    { label: 'Priority', value: 'PRIORITY' },
    { label: 'Manual Only', value: 'MANUAL' }
  ];

  const skillMatchingOptions = [
    { label: 'Any Skill Match', value: 'ANY' },
    { label: 'All Skills Required', value: 'ALL' },
    { label: 'Best Match', value: 'BEST_MATCH' }
  ];

  const tabs = [
    {
      id: 'configuration',
      content: 'Configuration',
      panelID: 'configuration-panel'
    },
    {
      id: 'workload',
      content: 'Agent Workload',
      panelID: 'workload-panel'
    },
    {
      id: 'analytics',
      content: 'Analytics',
      panelID: 'analytics-panel'
    }
  ];

  // Configuration Tab Content
  const configurationTab = (
    <Layout>
      <Layout.Section>
        {!config ? (
          <Card>
            <EmptyState
              heading="Set up team routing"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              action={{
                content: 'Configure Routing',
                onAction: () => setShowConfigModal(true)
              }}
            >
              <p>
                Configure intelligent routing to automatically assign conversations to your team
                members based on availability, skills, and workload.
              </p>
            </EmptyState>
          </Card>
        ) : (
          <>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    Routing Configuration
                  </Text>
                  <Button onClick={() => setShowConfigModal(true)} icon={EditMinor}>
                    Edit Configuration
                  </Button>
                </InlineStack>

                <BlockStack gap="300">
                  <InlineStack gap="200" blockAlign="center">
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      Status:
                    </Text>
                    <Badge status={config.isActive ? 'success' : 'critical'}>
                      {config.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </InlineStack>

                  <InlineStack gap="200" blockAlign="center">
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      Strategy:
                    </Text>
                    <Badge status="info">{config.strategy.replace('_', ' ')}</Badge>
                  </InlineStack>

                  <InlineStack gap="200" blockAlign="center">
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      Fallback Strategy:
                    </Text>
                    <Badge>{config.fallbackStrategy.replace('_', ' ')}</Badge>
                  </InlineStack>

                  <InlineStack gap="200" blockAlign="center">
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      Max Conversations per Agent:
                    </Text>
                    <Text as="span" variant="bodyMd">
                      {config.maxConversationsPerAgent}
                    </Text>
                  </InlineStack>

                  <InlineStack gap="200" blockAlign="center">
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      Business Hours Only:
                    </Text>
                    <Badge status={config.businessHoursOnly ? 'attention' : 'info'}>
                      {config.businessHoursOnly ? 'Yes' : 'No'}
                    </Badge>
                  </InlineStack>

                  <InlineStack gap="200" blockAlign="center">
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      Load Balancing:
                    </Text>
                    <Badge status={config.enableLoadBalancing ? 'success' : 'critical'}>
                      {config.enableLoadBalancing ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </InlineStack>

                  <InlineStack gap="200" blockAlign="center">
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      Skill-Based Routing:
                    </Text>
                    <Badge status={config.skillBasedRouting ? 'success' : 'critical'}>
                      {config.skillBasedRouting ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </InlineStack>

                  {config.skillBasedRouting && (
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        Skill Matching Mode:
                      </Text>
                      <Badge>{config.skillMatchingMode.replace('_', ' ')}</Badge>
                    </InlineStack>
                  )}
                </BlockStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    Team Members
                  </Text>
                  <Button onClick={fetchAgents} icon={RefreshMinor}>
                    Refresh
                  </Button>
                </InlineStack>

                {agents.length === 0 ? (
                  <Banner status="warning">
                    <p>No team members found. Add agents to start using team routing.</p>
                  </Banner>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'text']}
                    headings={['Name', 'Email', 'Skills', 'Max Conv.', 'Available', 'Actions']}
                    rows={agents.map(agent => [
                      agent.name,
                      agent.email,
                      agent.skills.length > 0 ? agent.skills.join(', ') : '-',
                      agent.maxConversations.toString(),
                      <Badge status={agent.isAvailable ? 'success' : 'critical'}>
                        {agent.isAvailable ? 'Available' : 'Unavailable'}
                      </Badge>,
                      <Button onClick={() => handleEditAgent(agent)} size="slim" icon={EditMinor}>
                        Edit
                      </Button>
                    ])}
                  />
                )}
              </BlockStack>
            </Card>
          </>
        )}
      </Layout.Section>
    </Layout>
  );

  // Workload Tab Content
  const workloadTab = (
    <Layout>
      <Layout.Section>
        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h2" variant="headingMd">
                Real-Time Agent Workload
              </Text>
              <Button onClick={fetchWorkloads} icon={RefreshMinor}>
                Refresh
              </Button>
            </InlineStack>

            {workloads.length === 0 ? (
              <Banner status="info">
                <p>No workload data available. Configure routing to start tracking agent workload.</p>
              </Banner>
            ) : (
              <BlockStack gap="400">
                {workloads.map(workload => (
                  <Card key={workload.agentId}>
                    <BlockStack gap="300">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="h3" variant="headingSm">
                          {workload.agentName}
                        </Text>
                        <Badge status={workload.isAvailable ? 'success' : 'critical'}>
                          {workload.isAvailable ? 'Available' : 'Unavailable'}
                        </Badge>
                      </InlineStack>

                      <BlockStack gap="200">
                        <Text as="p" variant="bodyMd">
                          Active: {workload.activeConversations} / {workload.maxConversations}
                        </Text>
                        <ProgressBar
                          progress={workload.utilization}
                          size="small"
                          tone={
                            workload.utilization >= 90
                              ? 'critical'
                              : workload.utilization >= 70
                              ? 'caution'
                              : 'success'
                          }
                        />
                        <Text as="p" variant="bodySm" tone="subdued">
                          Utilization: {workload.utilization.toFixed(1)}%
                        </Text>
                      </BlockStack>

                      {workload.skills.length > 0 && (
                        <InlineStack gap="100">
                          <Text as="span" variant="bodyMd" fontWeight="semibold">
                            Skills:
                          </Text>
                          {workload.skills.map(skill => (
                            <Badge key={skill}>{skill}</Badge>
                          ))}
                        </InlineStack>
                      )}
                    </BlockStack>
                  </Card>
                ))}
              </BlockStack>
            )}
          </BlockStack>
        </Card>
      </Layout.Section>
    </Layout>
  );

  // Analytics Tab Content
  const analyticsTab = (
    <Layout>
      <Layout.Section>
        {statistics && (
          <Layout>
            <Layout.Section oneHalf>
              <Card>
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">
                    Routing Statistics (Last 7 Days)
                  </Text>
                  <BlockStack gap="200">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        Total Routed:
                      </Text>
                      <Text as="span" variant="bodyLg">
                        {statistics.totalRouted}
                      </Text>
                    </InlineStack>

                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        Successful:
                      </Text>
                      <Text as="span" variant="bodyLg">
                        {statistics.successfulRoutes}
                      </Text>
                      <Badge status="success">
                        {((statistics.successfulRoutes / statistics.totalRouted) * 100).toFixed(1)}%
                      </Badge>
                    </InlineStack>

                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        Failed:
                      </Text>
                      <Text as="span" variant="bodyLg">
                        {statistics.failedRoutes}
                      </Text>
                      <Badge status="critical">
                        {((statistics.failedRoutes / statistics.totalRouted) * 100).toFixed(1)}%
                      </Badge>
                    </InlineStack>

                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        Avg. Routing Time:
                      </Text>
                      <Text as="span" variant="bodyLg">
                        {statistics.averageRoutingTime.toFixed(0)}ms
                      </Text>
                    </InlineStack>
                  </BlockStack>
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section oneHalf>
              <Card>
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">
                    Strategy Usage
                  </Text>
                  <BlockStack gap="200">
                    {Object.entries(statistics.strategyBreakdown).map(([strategy, count]) => (
                      <InlineStack key={strategy} gap="200" blockAlign="center">
                        <Text as="span" variant="bodyMd" fontWeight="semibold">
                          {strategy.replace('_', ' ')}:
                        </Text>
                        <Text as="span" variant="bodyLg">
                          {count}
                        </Text>
                        <Badge>
                          {((count / statistics.totalRouted) * 100).toFixed(1)}%
                        </Badge>
                      </InlineStack>
                    ))}
                  </BlockStack>
                </BlockStack>
              </Card>
            </Layout.Section>
          </Layout>
        )}

        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h2" variant="headingMd">
                Agent Performance
              </Text>
              <Button onClick={fetchPerformance} icon={RefreshMinor}>
                Refresh
              </Button>
            </InlineStack>

            {performance.length === 0 ? (
              <Banner status="info">
                <p>No performance data available yet.</p>
              </Banner>
            ) : (
              <DataTable
                columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric']}
                headings={[
                  'Agent',
                  'Total Assigned',
                  'Completed',
                  'Active',
                  'Avg. Response Time'
                ]}
                rows={performance.map(perf => [
                  perf.agentName,
                  perf.totalAssigned.toString(),
                  perf.completedConversations.toString(),
                  perf.activeConversations.toString(),
                  perf.averageResponseTime
                    ? `${(perf.averageResponseTime / 60000).toFixed(1)} min`
                    : 'N/A'
                ])}
              />
            )}
          </BlockStack>
        </Card>
      </Layout.Section>
    </Layout>
  );

  // Configuration Modal
  const configModal = (
    <Modal
      open={showConfigModal}
      onClose={() => setShowConfigModal(false)}
      title="Routing Configuration"
      primaryAction={{
        content: 'Save',
        onAction: handleConfigSubmit
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: () => setShowConfigModal(false)
        }
      ]}
      large
    >
      <Modal.Section>
        <FormLayout>
          <Select
            label="Primary Routing Strategy"
            options={strategyOptions}
            value={configForm.strategy}
            onChange={(value) => setConfigForm({ ...configForm, strategy: value as any })}
            helpText="How conversations should be assigned to agents"
          />

          <Select
            label="Fallback Strategy"
            options={strategyOptions}
            value={configForm.fallbackStrategy}
            onChange={(value) => setConfigForm({ ...configForm, fallbackStrategy: value as any })}
            helpText="Used when primary strategy fails"
          />

          <TextField
            label="Max Conversations per Agent"
            type="number"
            value={configForm.maxConversationsPerAgent.toString()}
            onChange={(value) =>
              setConfigForm({
                ...configForm,
                maxConversationsPerAgent: parseInt(value) || 10
              })
            }
            helpText="Maximum active conversations per agent"
            autoComplete="off"
          />

          <Checkbox
            label="Enable Load Balancing"
            checked={configForm.enableLoadBalancing}
            onChange={(value) => setConfigForm({ ...configForm, enableLoadBalancing: value })}
            helpText="Distribute conversations evenly across agents"
          />

          <Checkbox
            label="Business Hours Only"
            checked={configForm.businessHoursOnly}
            onChange={(value) => setConfigForm({ ...configForm, businessHoursOnly: value })}
            helpText="Only route during configured business hours"
          />

          <TextField
            label="Timezone"
            value={configForm.timezone}
            onChange={(value) => setConfigForm({ ...configForm, timezone: value })}
            placeholder="UTC, America/New_York, Asia/Kolkata"
            autoComplete="off"
          />

          <Checkbox
            label="Enable Skill-Based Routing"
            checked={configForm.skillBasedRouting}
            onChange={(value) => setConfigForm({ ...configForm, skillBasedRouting: value })}
            helpText="Match conversations to agents based on skills"
          />

          {configForm.skillBasedRouting && (
            <Select
              label="Skill Matching Mode"
              options={skillMatchingOptions}
              value={configForm.skillMatchingMode}
              onChange={(value) =>
                setConfigForm({ ...configForm, skillMatchingMode: value as any })
              }
              helpText="How to match conversation skills with agent skills"
            />
          )}

          <Checkbox
            label="Assign to Any Agent if None Available"
            checked={configForm.assignToAnyIfUnavailable}
            onChange={(value) =>
              setConfigForm({ ...configForm, assignToAnyIfUnavailable: value })
            }
            helpText="Fallback to any agent when no suitable agent is available"
          />

          <Checkbox
            label="Active"
            checked={configForm.isActive}
            onChange={(value) => setConfigForm({ ...configForm, isActive: value })}
            helpText="Enable or disable routing system"
          />
        </FormLayout>
      </Modal.Section>
    </Modal>
  );

  // Agent Modal
  const agentModal = (
    <Modal
      open={showAgentModal}
      onClose={() => {
        setShowAgentModal(false);
        resetAgentForm();
      }}
      title={`Edit Agent: ${editingAgent?.name}`}
      primaryAction={{
        content: 'Save',
        onAction: handleAgentUpdate
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: () => {
            setShowAgentModal(false);
            resetAgentForm();
          }
        }
      ]}
    >
      <Modal.Section>
        <FormLayout>
          <TextField
            label="Skills (comma-separated)"
            value={agentForm.skills}
            onChange={(value) => setAgentForm({ ...agentForm, skills: value })}
            placeholder="sales, technical, billing"
            helpText="Enter skills separated by commas"
            autoComplete="off"
          />

          <TextField
            label="Max Conversations"
            type="number"
            value={agentForm.maxConversations.toString()}
            onChange={(value) =>
              setAgentForm({ ...agentForm, maxConversations: parseInt(value) || 10 })
            }
            helpText="Maximum active conversations for this agent"
            autoComplete="off"
          />

          <Checkbox
            label="Available for Routing"
            checked={agentForm.isAvailable}
            onChange={(value) => setAgentForm({ ...agentForm, isAvailable: value })}
            helpText="Should this agent receive new conversations?"
          />
        </FormLayout>
      </Modal.Section>
    </Modal>
  );

  if (loading) {
    return (
      <Page title="Team Routing">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Frame>
      <Page
        title="Team Routing"
        primaryAction={
          config
            ? {
                content: 'Edit Configuration',
                icon: EditMinor,
                onAction: () => setShowConfigModal(true)
              }
            : undefined
        }
      >
        <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
          <div style={{ marginTop: '16px' }}>
            {selectedTab === 0 && configurationTab}
            {selectedTab === 1 && workloadTab}
            {selectedTab === 2 && analyticsTab}
          </div>
        </Tabs>

        {configModal}
        {agentModal}

        {toastActive && (
          <Toast
            content={toastMessage}
            onDismiss={() => setToastActive(false)}
            duration={3000}
          />
        )}
      </Page>
    </Frame>
  );
}
